import {
  Modal,
  Form,
  Row,
  Col,
  DatePicker,
  Select,
  Input,
  Table,
  Spin,
} from "antd";
import React, { useEffect, useState } from "react";
import "./NotesManager.css";
import ChantButton from "../../../../CommonComponents/ChantButton/ChantButton";
import {
  GROUPS_CHANNEL,
  GROUPS_LEAD_SOURCE,
  GROUPS_OWNER,
  GROUPS_STAGE,
} from "../../../../constants/superadmin/groups";
import dayjs from "dayjs";
import logger from "../../../../utils/logger";
import ChantCheckbox from "../../../../CommonComponents/ChantCheckbox/ChantCheckbox";
import useGlobalStore from "../../../../store/store";
import { chantApi } from "../../../../utils/chantApi";

const NotesManager = ({ showModal, setShowModal, groupData, setGroupData }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);

  const { updateGroup } = useGlobalStore((state) => state.superadmin.groups);

  const resetForm = () => {
    form.resetFields();
    setNotes([]);
    setGroupData(null);
  };

  const handleCancel = () => {
    setShowModal(false);
    resetForm();
  };

  const getNotes = async (groupId) => {
    if (!groupId) {
      logger.warn("No group ID provided to getNotes");
      setNotes([]);
      return;
    }

    try {
      setNotesLoading(true);
      const { data } = await chantApi.get(
        `${import.meta.env.VITE_GCP_BASE_URL}/superadmin/groups/${groupId}/notes`,
      );
      const notesArray = data?.notes || [];

      const sortedNotesArray = notesArray.sort(
        (a, b) => b.createdAt - a.createdAt,
      );

      setNotes(sortedNotesArray);

      if (sortedNotesArray.length > 0) {
        const mostRecentNote = sortedNotesArray[0];
        if (mostRecentNote?.channel) {
          form.setFieldsValue({
            channel: mostRecentNote.channel,
          });
        }
      }
    } catch (error) {
      logger.error("Error in getNotes:", error);
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  const postNotes = async (groupId, values) => {
    try {
      // Update account status
      await chantApi.put(
        `${import.meta.env.VITE_GCP_BASE_URL}/superadmin/groups/account-status`,
        {
          groupId,
          nextOutreachDate: values.nextOutreachDate?.valueOf() || null,
          status: values.stage,
          owner: values.owner,
          leadSource: values.leadSource,
          priorityGroup: values.priorityGroup,
          socialAnnouncement: values.socialAnnouncement,
          websiteInvoiced: values.websiteInvoiced?.valueOf() || null,
        },
      );

      // Add note
      await chantApi.post(
        `${import.meta.env.VITE_GCP_BASE_URL}/superadmin/groups/notes`,
        {
          groupId,
          nextOutreachDate: values.nextOutreachDate?.valueOf() || null,
          channel: values.channel,
          notes: values.notes,
          createdAt: dayjs().valueOf(),
        },
      );

      setShowModal(false);
      setGroupData(null);
      form.resetFields();

      // Fetch updated group data
      const { data } = await chantApi.get(
        `${import.meta.env.VITE_GCP_BASE_URL}/superadmin/groups/${groupId}/enriched`,
      );

      updateGroup(groupId, data);
    } catch (error) {
      logger.error("Error in postNotes:", error);
      throw error;
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Create payload with only valid values
      await postNotes(groupData?.id, values);
    } catch (error) {
      logger.error("Error in handleSubmit:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      width: 100,
      title: "Added",
      key: "added",
      render: () => groupData?.owner,
    },
    {
      width: 100,
      title: "Date",
      key: "date",
      dataIndex: "createdAt",
      render: (data) => dayjs(data).format("MMM DD, YYYY"),
    },
    {
      width: 100,
      title: "Channel",
      key: "channel",
      dataIndex: "channel",
    },
    {
      width: 100,
      title: "Status",
      key: "status",
      render: () => groupData?.status,
    },
    {
      width: 100,
      title: "Outreach",
      key: "outreach",
      render: () =>
        groupData?.nextOutreachDate
          ? dayjs(groupData?.nextOutreachDate).format("MMM DD, YYYY")
          : "",
    },
    {
      width: 300,
      title: "Notes",
      key: "notes",
      dataIndex: "notes",
    },
  ];

  useEffect(() => {
    if (groupData?.id) {
      getNotes(groupData?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData?.id]);

  // Add new effect to populate form fields
  useEffect(() => {
    if (showModal && groupData) {
      form.setFieldsValue({
        nextOutreachDate: groupData?.nextOutreachDate
          ? dayjs(groupData?.nextOutreachDate)
          : null,
        stage: groupData.status || null,
        owner: groupData.owner || null,
        leadSource: groupData.leadSource || null,
        priorityGroup: groupData.priorityGroup || false,
        socialAnnouncement: groupData.socialAnnouncement || false,
        websiteInvoiced: groupData?.websiteInvoiced
          ? dayjs(groupData?.websiteInvoiced)
          : null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, groupData]);

  return (
    <Modal
      title="Account Status"
      open={showModal}
      onCancel={handleCancel}
      width={1100}
      footer={null}
      className="sa-groups-add-notes-modal"
    >
      {notesLoading ? (
        <div className="sa-groups-add-notes-loading">
          <Spin size="large" />
        </div>
      ) : (
        <div
          className="sa-groups-add-notes-container"
          style={{ padding: "24px" }}
        >
          <Form
            layout="vertical"
            style={{ width: "100%" }}
            form={form}
            onFinish={handleSubmit}
            loading={loading}
          >
            <Row gutter={24} style={{ width: "100%" }}>
              <Col span={12}>
                <Form.Item
                  label="Next outreach"
                  style={{ width: "100%" }}
                  name="nextOutreachDate"
                >
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item
                  label="Channel"
                  style={{ width: "100%" }}
                  name="channel"
                >
                  <Select
                    placeholder="Select channel"
                    options={[
                      ...GROUPS_CHANNEL.map((item) => ({
                        label: item,
                        value: item,
                      })),
                    ]}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
                <Form.Item label="Stage" style={{ width: "100%" }} name="stage">
                  <Select
                    placeholder="Select stage"
                    options={GROUPS_STAGE.map((item) => ({
                      label: item,
                      value: item,
                    }))}
                    style={{ width: "100%" }}
                  />
                </Form.Item>

                <Form.Item name="priorityGroup" valuePropName="checked">
                  <ChantCheckbox>Priority Group</ChantCheckbox>
                </Form.Item>

                <Form.Item name="socialAnnouncement" valuePropName="checked">
                  <ChantCheckbox>
                    Social/Collaborative Announcement
                  </ChantCheckbox>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Owner" style={{ width: "100%" }} name="owner">
                  <Select
                    placeholder="Select owner"
                    options={GROUPS_OWNER.map((item) => ({
                      label: item,
                      value: item,
                    }))}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
                <Form.Item
                  label="Lead source"
                  style={{ width: "100%" }}
                  name="leadSource"
                >
                  <Select
                    placeholder="Select lead source"
                    options={GROUPS_LEAD_SOURCE.map((item) => ({
                      label: item,
                      value: item,
                    }))}
                    style={{ width: "100%" }}
                  />
                </Form.Item>

                <Form.Item
                  label="Website Invoiced"
                  style={{ width: "100%" }}
                  name="websiteInvoiced"
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format="MMM DD, YYYY"
                    placeholder="Set website invoice date"
                  />
                </Form.Item>

                <Form.Item label="Notes" style={{ width: "100%" }} name="notes">
                  <Input.TextArea
                    placeholder="Enter notes"
                    rows={4}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item style={{ width: "100%" }} className="flex-center">
              <ChantButton type="primary" htmlType="submit" loading={loading}>
                Submit
              </ChantButton>
            </Form.Item>
          </Form>

          {notes?.length > 0 && (
            <div className="sa-groups-add-notes-history">
              <h6 className="pb-2">History</h6>
              <div className="sa-groups-add-notes-history-table">
                <Table
                  sticky={{
                    offsetHeader: 0,
                    getContainer: () => document.body,
                  }}
                  bordered
                  headerBorderRadius={0}
                  columns={columns}
                  pagination={false}
                  dataSource={notes}
                  scroll={{ x: "max-content" }}
                  loading={notesLoading}
                  rowKey="id"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default NotesManager;
